/**
 * Local AI Service
 *
 * Provides AI-powered recommendations using local Ollama/Llama models
 * This service is optional and enhances decision-making without requiring cloud APIs
 */

import { EventEmitter } from 'events';
import { isLocalApiUnavailable } from '../utils/devApi';

interface LocalAIConfig {
  enabled: boolean;
  modelName: string;
  baseUrl: string;
  timeout: number;
}

interface AIRecommendation {
  recommendation: string;
  confidence: number;
  reasoning: string;
  warnings?: string[];
  suggestions?: string[];
}

class LocalAIService extends EventEmitter {
  private static instance: LocalAIService;
  private config: LocalAIConfig;
  private isModelAvailable: boolean = false;

  private constructor() {
    super();
    this.config = this.loadConfig();
    this.detectModel();
  }

  static getInstance(): LocalAIService {
    if (!LocalAIService.instance) {
      LocalAIService.instance = new LocalAIService();
    }
    return LocalAIService.instance;
  }

  /**
   * Load configuration from localStorage or defaults
   */
  private loadConfig(): LocalAIConfig {
    try {
      const stored = localStorage.getItem('slikpos_local_ai_config');
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.warn('Failed to load local AI config:', error);
    }

    // Default configuration
    return {
      enabled: true,
      modelName: 'llama2', // Default Ollama model
      baseUrl: 'http://localhost:11434',
      timeout: 10000 // 10 seconds
    };
  }

  /**
   * Save configuration to localStorage
   */
  private saveConfig(): void {
    try {
      localStorage.setItem('slikpos_local_ai_config', JSON.stringify(this.config));
    } catch (error) {
      console.error('Failed to save local AI config:', error);
    }
  }

  /**
   * Detect if local AI model is available
   */
  private async detectModel(): Promise<void> {
    if (!this.config.enabled) {
      console.log('🤖 Local AI disabled in settings');
      return;
    }

    // On the deployed site the localhost Ollama endpoint is unreachable — skip detection silently.
    if (isLocalApiUnavailable(this.config.baseUrl)) {
      this.isModelAvailable = false;
      return;
    }

    try {
      const response = await fetch(`${this.config.baseUrl}/api/tags`, {
        method: 'GET',
        signal: AbortSignal.timeout(3000) // 3 second timeout for detection
      });

      if (response.ok) {
        const data = await response.json();
        const hasModel = data.models?.some((m: any) =>
          m.name.includes(this.config.modelName) || m.name.includes('llama')
        );

        if (hasModel) {
          this.isModelAvailable = true;
          console.log('✅ Local AI model available:', this.config.modelName);
          this.emit('model-available', this.config.modelName);
        } else {
          console.log('⚠️ Ollama running but model not found:', this.config.modelName);
          this.emit('model-not-found', this.config.modelName);
        }
      }
    } catch (error) {
      console.log('ℹ️ Local AI model not available (Ollama not running)');
      this.isModelAvailable = false;
    }
  }

  /**
   * Check if local AI is available
   */
  isAvailable(): boolean {
    return this.config.enabled && this.isModelAvailable;
  }

  /**
   * Generate AI completion using local model
   */
  private async generateCompletion(prompt: string): Promise<string> {
    if (!this.isAvailable()) {
      throw new Error('Local AI model not available');
    }

    try {
      const response = await fetch(`${this.config.baseUrl}/api/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          model: this.config.modelName,
          prompt: prompt,
          stream: false,
          options: {
            temperature: 0.3, // Lower temperature for more focused responses
            num_predict: 256  // Shorter responses for speed
          }
        }),
        signal: AbortSignal.timeout(this.config.timeout)
      });

      if (!response.ok) {
        throw new Error(`AI request failed: ${response.statusText}`);
      }

      const data = await response.json();
      return data.response || '';
    } catch (error) {
      console.error('AI generation error:', error);
      throw error;
    }
  }

  /**
   * Analyze emergency booking squeeze-in decision
   *
   * @param context - Booking context (appointments, barber, timing)
   * @returns AI recommendation with confidence score
   */
  async analyzeEmergencyBooking(context: {
    targetTime: string;
    originalDuration: number;
    shrunkDuration: number;
    emergencyDuration: number;
    nextAppointmentTime?: string;
    bufferMinutes?: number;
    barberName: string;
    urgencyLevel: 'standard' | 'urgent' | 'critical';
  }): Promise<AIRecommendation> {
    if (!this.isAvailable()) {
      // Fallback: Rule-based recommendation
      return this.getRuleBasedRecommendation(context);
    }

    try {
      const prompt = this.buildEmergencyBookingPrompt(context);
      const aiResponse = await this.generateCompletion(prompt);
      return this.parseAIResponse(aiResponse, context);
    } catch (error) {
      console.warn('AI analysis failed, falling back to rule-based:', error);
      return this.getRuleBasedRecommendation(context);
    }
  }

  /**
   * Build prompt for emergency booking analysis
   */
  private buildEmergencyBookingPrompt(context: any): string {
    return `You are an AI assistant for a barbershop point-of-sale system. Analyze this emergency booking request:

Target Time: ${context.targetTime}
Original Appointment: ${context.originalDuration} minutes
Will Shrink To: ${context.shrunkDuration} minutes
Emergency Booking: ${context.emergencyDuration} minutes
Next Appointment: ${context.nextAppointmentTime || 'None'}
Buffer Time: ${context.bufferMinutes || 0} minutes
Barber: ${context.barberName}
Urgency: ${context.urgencyLevel}

Provide a brief recommendation (2-3 sentences) on whether this squeeze-in is safe, and any warnings or suggestions. Be concise and professional.`;
  }

  /**
   * Parse AI response into structured recommendation
   */
  private parseAIResponse(response: string, context: any): AIRecommendation {
    // Extract confidence from response or use default
    let confidence = 0.7;
    if (response.toLowerCase().includes('safe') || response.toLowerCase().includes('recommend')) {
      confidence = 0.85;
    }
    if (response.toLowerCase().includes('tight') || response.toLowerCase().includes('warning')) {
      confidence = 0.6;
    }
    if (response.toLowerCase().includes('not recommend') || response.toLowerCase().includes('risky')) {
      confidence = 0.4;
    }

    // Extract warnings
    const warnings: string[] = [];
    if (response.toLowerCase().includes('tight') && context.bufferMinutes && context.bufferMinutes < 5) {
      warnings.push('Very tight buffer before next appointment');
    }

    // Extract suggestions
    const suggestions: string[] = [];
    if (response.toLowerCase().includes('call') || response.toLowerCase().includes('notify')) {
      suggestions.push('Consider notifying next client about potential delay');
    }

    return {
      recommendation: response.trim(),
      confidence,
      reasoning: this.extractReasoning(response),
      warnings: warnings.length > 0 ? warnings : undefined,
      suggestions: suggestions.length > 0 ? suggestions : undefined
    };
  }

  /**
   * Extract reasoning from AI response
   */
  private extractReasoning(response: string): string {
    // Try to find reasoning in response (sentences with "because", "due to", etc.)
    const reasoningMarkers = ['because', 'due to', 'since', 'given that'];
    for (const marker of reasoningMarkers) {
      const index = response.toLowerCase().indexOf(marker);
      if (index !== -1) {
        // Extract sentence containing reasoning
        const remaining = response.substring(index);
        const endIndex = remaining.indexOf('.') !== -1 ? remaining.indexOf('.') : remaining.length;
        return remaining.substring(0, endIndex + 1).trim();
      }
    }
    return 'AI analysis complete';
  }

  /**
   * Rule-based fallback recommendation (when AI is unavailable)
   */
  private getRuleBasedRecommendation(context: any): AIRecommendation {
    const warnings: string[] = [];
    const suggestions: string[] = [];
    let confidence = 0.8;
    let recommendation = '';

    // Check buffer time
    if (context.bufferMinutes !== undefined) {
      if (context.bufferMinutes < 3) {
        warnings.push('Very tight buffer (<3 min) before next appointment');
        confidence = 0.5;
        recommendation = '⚠️ Tight schedule - squeeze-in may cause delays for next client.';
      } else if (context.bufferMinutes < 5) {
        warnings.push('Limited buffer time before next appointment');
        confidence = 0.65;
        recommendation = '⚠️ Limited buffer time - monitor closely to stay on schedule.';
      } else {
        recommendation = '✅ Safe to proceed - adequate buffer time before next appointment.';
      }
    } else {
      recommendation = '✅ Safe to proceed - no immediate appointments following.';
    }

    // Check shrink ratio
    const shrinkRatio = context.shrunkDuration / context.originalDuration;
    if (shrinkRatio < 0.5) {
      warnings.push('Original appointment shrunk by >50%');
      suggestions.push('Confirm with original client that shortened service is acceptable');
      confidence *= 0.9;
    }

    // Urgency level adjustments
    if (context.urgencyLevel === 'critical') {
      suggestions.push('High urgency - prioritize this emergency booking');
    }

    return {
      recommendation,
      confidence,
      reasoning: `Rule-based analysis: ${warnings.length > 0 ? 'Concerns identified' : 'No major concerns'}`,
      warnings: warnings.length > 0 ? warnings : undefined,
      suggestions: suggestions.length > 0 ? suggestions : undefined
    };
  }

  /**
   * Update configuration
   */
  updateConfig(updates: Partial<LocalAIConfig>): void {
    this.config = { ...this.config, ...updates };
    this.saveConfig();

    if (updates.enabled !== undefined || updates.baseUrl !== undefined || updates.modelName !== undefined) {
      this.detectModel();
    }

    this.emit('config-updated', this.config);
  }

  /**
   * Get current configuration
   */
  getConfig(): LocalAIConfig {
    return { ...this.config };
  }

  /**
   * Get model status
   */
  getStatus(): {
    enabled: boolean;
    available: boolean;
    model: string;
    url: string;
  } {
    return {
      enabled: this.config.enabled,
      available: this.isModelAvailable,
      model: this.config.modelName,
      url: this.config.baseUrl
    };
  }

  // ============================================
  // ADDITIONAL AI CAPABILITIES
  // ============================================

  /**
   * Optimize schedule for maximum efficiency
   *
   * Analyzes current appointments and suggests optimal scheduling changes
   */
  async optimizeSchedule(context: {
    appointments: any[];
    barbers: any[];
    timeRange: { start: string; end: string };
  }): Promise<{
    suggestions: Array<{
      type: 'move' | 'merge' | 'gap-fill' | 'break';
      appointmentId?: string;
      currentTime?: string;
      suggestedTime?: string;
      reasoning: string;
      benefitMinutes?: number;
    }>;
    confidence: number;
  }> {
    if (!this.isAvailable()) {
      return this.getRuleBasedScheduleOptimization(context);
    }

    try {
      const prompt = `Analyze this barbershop schedule and suggest 2-3 optimizations:

Appointments: ${context.appointments.length} scheduled
Barbers: ${context.barbers.length} available
Time Range: ${context.timeRange.start} - ${context.timeRange.end}

Suggest ways to:
- Reduce gaps between appointments
- Balance barber workload
- Create efficient break times
- Maximize daily revenue

Be specific and concise (2-3 sentences per suggestion).`;

      const response = await this.generateCompletion(prompt);
      return this.parseScheduleOptimization(response);
    } catch (error) {
      console.warn('Schedule optimization AI failed, using fallback');
      return this.getRuleBasedScheduleOptimization(context);
    }
  }

  /**
   * Predict client retention risk
   *
   * Analyzes client behavior to predict churn risk
   */
  async predictClientRetention(client: {
    id: string;
    name: string;
    lastVisit?: Date;
    totalVisits: number;
    totalSpent: number;
    averageVisitInterval: number; // days
    noShowCount: number;
    memberSince?: Date;
  }): Promise<{
    riskLevel: 'low' | 'medium' | 'high' | 'critical';
    confidence: number;
    reasoning: string;
    recommendations: string[];
  }> {
    const daysSinceLastVisit = client.lastVisit
      ? Math.floor((Date.now() - new Date(client.lastVisit).getTime()) / (1000 * 60 * 60 * 24))
      : 999;

    // Rule-based fallback (always available)
    if (!this.isAvailable()) {
      return this.getRuleBasedRetentionPrediction(client, daysSinceLastVisit);
    }

    try {
      const prompt = `Analyze client retention risk:

Client: ${client.name}
Last Visit: ${daysSinceLastVisit} days ago
Total Visits: ${client.totalVisits}
Average Interval: ${client.averageVisitInterval} days
No-Shows: ${client.noShowCount}
Total Spent: $${client.totalSpent}

Assess churn risk (low/medium/high/critical) and suggest 2-3 retention actions. Be brief.`;

      const response = await this.generateCompletion(prompt);
      return this.parseRetentionPrediction(response, client, daysSinceLastVisit);
    } catch (error) {
      return this.getRuleBasedRetentionPrediction(client, daysSinceLastVisit);
    }
  }

  /**
   * Recommend optimal barber for client
   *
   * Considers client preferences, barber availability, and performance
   */
  async recommendBarber(context: {
    clientName: string;
    serviceType: string;
    clientHistory?: Array<{ barberId: string; satisfaction?: number }>;
    availableBarbers: Array<{
      id: string;
      name: string;
      specialty?: string;
      currentLoad: number; // appointments today
      averageSatisfaction: number;
    }>;
  }): Promise<{
    recommendedBarber: string;
    alternativeBarbers: string[];
    reasoning: string;
    confidence: number;
  }> {
    // Rule-based recommendation (always available)
    if (!this.isAvailable() || context.availableBarbers.length === 0) {
      return this.getRuleBasedBarberRecommendation(context);
    }

    try {
      const prompt = `Recommend best barber for this client:

Client: ${context.clientName}
Service: ${context.serviceType}
Available Barbers: ${context.availableBarbers.map(b => `${b.name} (${b.currentLoad} apts, ${b.averageSatisfaction}/5 rating)`).join(', ')}

Consider specialties, workload balance, and client history. Provide 1-2 sentence reasoning.`;

      const response = await this.generateCompletion(prompt);
      return this.parseBarberRecommendation(response, context);
    } catch (error) {
      return this.getRuleBasedBarberRecommendation(context);
    }
  }

  /**
   * Suggest additional services to upsell
   *
   * Analyzes client profile and current booking to suggest add-ons
   */
  async suggestUpsellServices(context: {
    clientName: string;
    currentService: string;
    clientSpendingLevel: 'low' | 'medium' | 'high';
    previousServices: string[];
    availableServices: string[];
  }): Promise<{
    suggestions: Array<{
      service: string;
      reasoning: string;
      expectedIncrease: number; // percentage
    }>;
    confidence: number;
  }> {
    if (!this.isAvailable()) {
      return this.getRuleBasedUpsellSuggestions(context);
    }

    try {
      const prompt = `Suggest 2-3 service upsells for:

Current Service: ${context.currentService}
Client Spending: ${context.clientSpendingLevel}
Previous Services: ${context.previousServices.slice(0, 5).join(', ')}
Available Add-ons: ${context.availableServices.slice(0, 8).join(', ')}

Suggest complementary services that make sense. Brief reasoning for each.`;

      const response = await this.generateCompletion(prompt);
      return this.parseUpsellSuggestions(response, context);
    } catch (error) {
      return this.getRuleBasedUpsellSuggestions(context);
    }
  }

  /**
   * Predict realistic service duration
   *
   * Estimates actual service time based on barber, client, and service type
   */
  async predictServiceDuration(context: {
    serviceType: string;
    standardDuration: number; // minutes
    barberExperience: 'junior' | 'mid' | 'senior';
    clientComplexity: 'simple' | 'medium' | 'complex'; // hair type, special requests
    timeOfDay: string; // HH:MM
  }): Promise<{
    predictedDuration: number;
    confidence: number;
    reasoning: string;
  }> {
    // Rule-based duration prediction
    if (!this.isAvailable()) {
      return this.getRuleBasedDurationPrediction(context);
    }

    try {
      const prompt = `Predict realistic service duration:

Service: ${context.serviceType} (standard: ${context.standardDuration} min)
Barber: ${context.barberExperience} level
Client Complexity: ${context.clientComplexity}
Time: ${context.timeOfDay}

Account for: barber speed, client needs, time of day fatigue. Predict realistic minutes.`;

      const response = await this.generateCompletion(prompt);
      return this.parseDurationPrediction(response, context);
    } catch (error) {
      return this.getRuleBasedDurationPrediction(context);
    }
  }

  /**
   * Generate marketing insights and promotion suggestions
   */
  async generateMarketingInsights(context: {
    slowPeriods: Array<{ day: string; time: string }>;
    topClients: number;
    averageSpend: number;
    seasonalTrends: string[];
  }): Promise<{
    insights: string[];
    promotionSuggestions: Array<{
      type: string;
      targeting: string;
      expectedImpact: string;
    }>;
  }> {
    if (!this.isAvailable()) {
      return this.getRuleBasedMarketingInsights(context);
    }

    try {
      const prompt = `Generate marketing insights for barbershop:

Slow Periods: ${context.slowPeriods.slice(0, 3).map(p => `${p.day} ${p.time}`).join(', ')}
Top Clients: ${context.topClients}
Avg Spend: $${context.averageSpend}

Suggest 2-3 targeted promotions to increase bookings during slow times. Be creative and specific.`;

      const response = await this.generateCompletion(prompt);
      return this.parseMarketingInsights(response);
    } catch (error) {
      return this.getRuleBasedMarketingInsights(context);
    }
  }

  // ============================================
  // RULE-BASED FALLBACKS
  // ============================================

  private getRuleBasedScheduleOptimization(context: any): any {
    const suggestions = [
      {
        type: 'gap-fill' as const,
        reasoning: 'Fill scheduling gaps to maximize daily capacity',
        benefitMinutes: 30
      }
    ];
    return { suggestions, confidence: 0.7 };
  }

  private getRuleBasedRetentionPrediction(client: any, daysSinceLastVisit: number): any {
    let riskLevel: 'low' | 'medium' | 'high' | 'critical' = 'low';
    const recommendations: string[] = [];

    if (daysSinceLastVisit > client.averageVisitInterval * 2) {
      riskLevel = 'high';
      recommendations.push('Send personalized re-engagement offer');
      recommendations.push('Follow up with phone call or SMS');
    } else if (daysSinceLastVisit > client.averageVisitInterval * 1.5) {
      riskLevel = 'medium';
      recommendations.push('Send reminder about upcoming haircut time');
    } else {
      recommendations.push('Client is active - continue excellent service');
    }

    if (client.noShowCount > 2) {
      riskLevel = 'high';
      recommendations.push('Implement appointment confirmation system');
    }

    return {
      riskLevel,
      confidence: 0.75,
      reasoning: `Client is ${daysSinceLastVisit} days since last visit (avg: ${client.averageVisitInterval} days)`,
      recommendations
    };
  }

  private getRuleBasedBarberRecommendation(context: any): any {
    const barbers = context.availableBarbers;
    if (barbers.length === 0) {
      return {
        recommendedBarber: 'No barbers available',
        alternativeBarbers: [],
        reasoning: 'No barbers currently available',
        confidence: 0
      };
    }

    // Find least loaded barber with good ratings
    const sorted = [...barbers].sort((a, b) => {
      const scoreA = (5 - a.currentLoad) + (a.averageSatisfaction * 2);
      const scoreB = (5 - b.currentLoad) + (b.averageSatisfaction * 2);
      return scoreB - scoreA;
    });

    return {
      recommendedBarber: sorted[0].name,
      alternativeBarbers: sorted.slice(1, 3).map(b => b.name),
      reasoning: `${sorted[0].name} has optimal balance of availability (${sorted[0].currentLoad} appointments) and satisfaction (${sorted[0].averageSatisfaction}/5)`,
      confidence: 0.8
    };
  }

  private getRuleBasedUpsellSuggestions(context: any): any {
    const suggestions = [];
    const current = context.currentService.toLowerCase();

    if (current.includes('haircut') && context.availableServices.some((s: string) => s.toLowerCase().includes('beard'))) {
      suggestions.push({
        service: 'Beard Trim',
        reasoning: 'Natural complement to haircut service',
        expectedIncrease: 30
      });
    }

    if (context.clientSpendingLevel === 'high') {
      suggestions.push({
        service: 'Premium Styling Products',
        reasoning: 'High-value client likely interested in premium products',
        expectedIncrease: 20
      });
    }

    return { suggestions, confidence: 0.7 };
  }

  private getRuleBasedDurationPrediction(context: any): any {
    let multiplier = 1.0;

    // Adjust for barber experience
    if (context.barberExperience === 'junior') multiplier += 0.2;
    if (context.barberExperience === 'senior') multiplier -= 0.1;

    // Adjust for complexity
    if (context.clientComplexity === 'complex') multiplier += 0.25;
    if (context.clientComplexity === 'simple') multiplier -= 0.1;

    // Adjust for time of day (fatigue)
    const hour = parseInt(context.timeOfDay.split(':')[0]);
    if (hour >= 17) multiplier += 0.1; // End of day fatigue

    const predictedDuration = Math.round(context.standardDuration * multiplier);

    return {
      predictedDuration,
      confidence: 0.75,
      reasoning: `Adjusted for ${context.barberExperience} barber and ${context.clientComplexity} complexity`
    };
  }

  private getRuleBasedMarketingInsights(context: any): any {
    const insights = [
      'Identify slow periods and target promotions to fill gaps',
      'Reward top clients with exclusive offers to maintain loyalty',
      'Create time-limited offers during historically slow periods'
    ];

    const promotionSuggestions = [
      {
        type: 'Early Bird Special',
        targeting: 'Morning slow periods',
        expectedImpact: '15-20% increase in morning bookings'
      },
      {
        type: 'Loyalty Rewards',
        targeting: 'Top 20% of clients',
        expectedImpact: 'Increased retention and referrals'
      }
    ];

    return { insights, promotionSuggestions };
  }

  // ============================================
  // RESPONSE PARSERS
  // ============================================

  private parseScheduleOptimization(response: string): any {
    // Simple parsing - in production, use more sophisticated NLP
    return {
      suggestions: [
        {
          type: 'gap-fill' as const,
          reasoning: response.substring(0, 150),
          benefitMinutes: 20
        }
      ],
      confidence: 0.75
    };
  }

  private parseRetentionPrediction(response: string, client: any, daysSinceLastVisit: number): any {
    const lower = response.toLowerCase();
    let riskLevel: 'low' | 'medium' | 'high' | 'critical' = 'medium';

    if (lower.includes('critical') || lower.includes('very high risk')) riskLevel = 'critical';
    else if (lower.includes('high risk')) riskLevel = 'high';
    else if (lower.includes('low risk')) riskLevel = 'low';

    return {
      riskLevel,
      confidence: 0.8,
      reasoning: response.substring(0, 200),
      recommendations: ['Contact client with personalized offer', 'Schedule follow-up']
    };
  }

  private parseBarberRecommendation(response: string, context: any): any {
    // Try to extract barber name from response
    const barbers = context.availableBarbers;
    let recommended = barbers[0].name;

    for (const barber of barbers) {
      if (response.toLowerCase().includes(barber.name.toLowerCase())) {
        recommended = barber.name;
        break;
      }
    }

    return {
      recommendedBarber: recommended,
      alternativeBarbers: barbers.slice(1, 3).map((b: any) => b.name),
      reasoning: response.substring(0, 150),
      confidence: 0.8
    };
  }

  private parseUpsellSuggestions(response: string, context: any): any {
    return {
      suggestions: [
        {
          service: 'Add-on Service',
          reasoning: response.substring(0, 100),
          expectedIncrease: 25
        }
      ],
      confidence: 0.75
    };
  }

  private parseDurationPrediction(response: string, context: any): any {
    // Try to extract duration number from response
    const match = response.match(/(\d+)\s*(min|minute)/i);
    const predictedDuration = match ? parseInt(match[1]) : Math.round(context.standardDuration * 1.1);

    return {
      predictedDuration,
      confidence: 0.8,
      reasoning: response.substring(0, 150)
    };
  }

  private parseMarketingInsights(response: string): any {
    return {
      insights: [response.substring(0, 200)],
      promotionSuggestions: [
        {
          type: 'Targeted Promotion',
          targeting: 'Identified segments',
          expectedImpact: 'Moderate increase in bookings'
        }
      ]
    };
  }
}

export const localAIService = LocalAIService.getInstance();
export default localAIService;
